import type { DataGridColumnSettings } from '@myosh/odin-components';
import { useCallback, useRef } from 'react';
import type { RecordResult } from '../@types/records';
import type { EntityReference } from '../@types/views';
import { useAppDispatch } from '../redux/hooks';
import { recordApi } from '../redux/services/record';
import { batchPromises, makeCancelable } from '../utils/promise-util';
import { forceAssert } from '../utils/type-guards';

export type LinkedRecordsMap = Record<number, RecordResult>;

const MAX_IDS_PER_REQUEST = 100;
const MAX_CONCURRENT_REQUESTS = 5;

interface CancelablePromise {
  promise: Promise<unknown>;
  cancel: () => void;
}

interface UseFetchLinkedRecordColumnValuesResult {
  fetchLinkedRecordColumnValues: (
    columns: DataGridColumnSettings[],
    recordsData: Record<string, unknown>[]
  ) => Promise<LinkedRecordsMap>;
  cancelLinkedRecordColumnValuesPendingRequests: () => void;
}

/**
 * Hook to fetch linked records in bulk for RECORD_LINK and REVERSE_RECORD_LINK columns in a data grid.
 * Returns a function to trigger the fetch that returns a map of record IDs to record data.
 */
export function useFetchLinkedRecordColumnValues(sourceFormId: number): UseFetchLinkedRecordColumnValuesResult {
  const dispatch = useAppDispatch();
  const cancelablePromisesRef = useRef<CancelablePromise[]>([]);

  const cancelLinkedRecordColumnValuesPendingRequests = useCallback(() => {
    cancelablePromisesRef.current.forEach((cancelable) => cancelable.cancel());
    cancelablePromisesRef.current = [];
  }, []);

  const fetchLinkedRecordColumnValuesBulk = useCallback(
    async (recordIds: Array<number>): Promise<Record<number, RecordResult>> => {
      const linkedRecordsMap: Record<number, RecordResult> = {};

      // If source form ID is invalid, return empty map
      if (!sourceFormId || sourceFormId <= 0) {
        return linkedRecordsMap;
      }

      // Build batch request functions - each batch has at most MAX_IDS_PER_REQUEST IDs
      const batchRequestFunctions: Array<() => Promise<Array<RecordResult>>> = [];

      // Split into chunks of MAX_IDS_PER_REQUEST
      for (let i = 0; i < recordIds.length; i += MAX_IDS_PER_REQUEST) {
        const chunk = recordIds.slice(i, i + MAX_IDS_PER_REQUEST);

        batchRequestFunctions.push(() =>
          dispatch(
            recordApi.endpoints.getLinkedRecordColumnValues.initiate({
              formId: sourceFormId,
              linkedRecordIds: chunk,
              keyTypeId: true,
            })
          ).unwrap()
        );
      }

      if (batchRequestFunctions.length === 0) {
        return linkedRecordsMap;
      }

      // Execute in batches of MAX_CONCURRENT_REQUESTS concurrent requests
      const results = await batchPromises(batchRequestFunctions, MAX_CONCURRENT_REQUESTS);

      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          result.value.forEach((item) => {
            if (item.id) {
              linkedRecordsMap[item.id] = item;
            }
          });
        }
      });

      return linkedRecordsMap;
    },
    [dispatch, sourceFormId]
  );

  const fetchLinkedRecordColumnValues = useCallback(
    async (columns: DataGridColumnSettings[], recordsData: Record<string, unknown>[]): Promise<LinkedRecordsMap> => {
      // If source form ID is invalid, return empty map
      if (!sourceFormId || sourceFormId <= 0) {
        return {};
      }

      // Cancel any pending requests from previous calls
      cancelLinkedRecordColumnValuesPendingRequests();

      // Extract RECORD_LINK and REVERSE_RECORD_LINK field entityIds
      const entityIds: Set<number> = new Set();

      columns.forEach((column) => {
        const columnEntityReference = forceAssert<EntityReference>(column.customDataProperties);
        const { entityId, entityType } = columnEntityReference;

        if (entityType === 'RECORD_LINK' || entityType === 'REVERSE_RECORD_LINK') {
          entityIds.add(entityId);
        }
      });

      // If there are no linked record IDs, return
      if (entityIds.size === 0) {
        return {};
      }

      // Add up all linked record IDs from the records data
      const linkedRecordIds: Set<number> = new Set();
      recordsData.forEach((record) => {
        entityIds.forEach((entityId) => {
          const ids = forceAssert<Record<string, Array<string>>>(record?.fields)?.[entityId];
          if (ids && Array.isArray(ids)) {
            ids.forEach((id) => linkedRecordIds.add(Number(id)));
          }
        });
      });

      // Check if there are any linked records to fetch
      if (linkedRecordIds.size === 0) {
        return {};
      }

      // Create a cancelable promise for the bulk fetch
      const cancelable = makeCancelable(fetchLinkedRecordColumnValuesBulk(Array.from(linkedRecordIds)));
      cancelablePromisesRef.current.push(cancelable);

      try {
        const linkedRecordsMap = (await cancelable.promise) as LinkedRecordsMap;
        return linkedRecordsMap;
      } catch (error) {
        // Check if this was a cancellation - don't log cancellations
        if (!(error as { isCanceled?: boolean })?.isCanceled) {
          console.error('Failed to fetch linked record column values', error);
        }
        return {};
      }
    },
    [fetchLinkedRecordColumnValuesBulk, cancelLinkedRecordColumnValuesPendingRequests, sourceFormId]
  );

  return {
    fetchLinkedRecordColumnValues,
    cancelLinkedRecordColumnValuesPendingRequests,
  };
}
